import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { MessageSquareShare, Send, ListChecks, BellRing } from "lucide-react";

const TOGGLES = [
  ["notify_new", "New rework / missing part submitted (pings optimisers)"],
  ["notify_hold", "Request placed On Hold (with reason)"],
  ["notify_complete", "Request completed"],
  ["notify_install", "Install team alerts — missing parts & maintenance items"],
  ["notify_digest", "Daily rack digest — 7:00am NZ summary of everything still in the rework rack"],
  ["notify_stale", "Stale item nudge — @mention the assigned lead when a part sits in the rack 7+ days"],
];

export default function TeamsSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [digesting, setDigesting] = useState(false);
  const [nudging, setNudging] = useState(false);

  useEffect(() => { api.get("/settings").then((r) => setS(r.data)).catch(() => toast.error("Failed to load settings")); }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings", s); setS(data); toast.success("Notification settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try { await api.post("/settings/teams-test"); toast.success("Test card posted to Teams"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Test failed"); }
    finally { setTesting(false); }
  };

  const digestNow = async () => {
    setDigesting(true);
    try { const { data } = await api.post("/settings/rack-digest"); toast.success(`Rack digest posted to Teams (${data.count} item${data.count === 1 ? "" : "s"})`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Digest failed"); }
    finally { setDigesting(false); }
  };

  const nudgeNow = async () => {
    setNudging(true);
    try { const { data } = await api.post("/settings/stale-nudge"); toast.success(data.stale ? `${data.sent} nudge${data.sent === 1 ? "" : "s"} sent (${data.stale} stale item${data.stale === 1 ? "" : "s"})` : "No parts have been in the rack 7+ days"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Nudge failed"); }
    finally { setNudging(false); }
  };

  if (!s) return null;
  return (
    <div className="border border-[#e6e5df] bg-white rounded-sm" data-testid="teams-settings">
      <div className="px-5 py-4 border-b border-[#e6e5df] flex items-center gap-3">
        <MessageSquareShare size={16} className="text-[#647265]" />
        <div className="font-display font-bold text-[#232826]">Microsoft Teams Notifications</div>
        <span className={`ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${s.teams_webhook_url ? "bg-emerald-50 text-emerald-700 border-emerald-300" : "bg-[#efece5] text-[#6b6a68] border-[#cfcfc9]"}`} data-testid="teams-status">
          {s.teams_webhook_url ? "Connected" : "Not connected"}
        </span>
      </div>
      <div className="p-5 space-y-5">
        <div>
          <Label className="text-xs uppercase tracking-widest text-[#6b6a68]">Incoming webhook URL</Label>
          <Input value={s.teams_webhook_url || ""} onChange={(e) => setS({ ...s, teams_webhook_url: e.target.value })} placeholder="https://….logic.azure.com/… or https://….webhook.office.com/…"
            data-testid="teams-webhook-input" className="bg-white border-[#cfcfc9] rounded-sm mt-2 text-[#232826] font-mono text-xs" />
          <p className="text-xs text-[#a39f98] mt-2">In Teams: channel → Workflows → "Post to a channel when a webhook request is received" → copy the URL. Until this is set, notifications only appear in the in-app feed.</p>
        </div>
        <div className="space-y-3">
          {TOGGLES.map(([k, label]) => (
            <label key={k} className="flex items-center justify-between gap-4 text-sm text-[#232826]">
              <span>{label}</span>
              <Switch checked={!!s[k]} onCheckedChange={(v) => setS({ ...s, [k]: v })} data-testid={`toggle-${k}`} />
            </label>
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          <Button onClick={save} disabled={saving} data-testid="teams-save" className="bg-[#424e43] hover:bg-[#232826] text-[#f7f5f1] rounded-sm uppercase text-xs tracking-widest">{saving ? "Saving…" : "Save"}</Button>
          <Button onClick={test} disabled={testing || !s.teams_webhook_url} variant="secondary" data-testid="teams-test" className="rounded-sm bg-[#e6e5df] hover:bg-[#cfcfc9] text-[#232826] border border-[#cfcfc9] uppercase text-xs tracking-widest">
            <Send size={13} className="mr-2" /> {testing ? "Sending…" : "Send test"}
          </Button>
          <Button onClick={digestNow} disabled={digesting || !s.teams_webhook_url} variant="secondary" data-testid="teams-digest-now" className="rounded-sm bg-[#e6e5df] hover:bg-[#cfcfc9] text-[#232826] border border-[#cfcfc9] uppercase text-xs tracking-widest">
            <ListChecks size={13} className="mr-2" /> {digesting ? "Posting…" : "Send digest now"}
          </Button>
          <Button onClick={nudgeNow} disabled={nudging || !s.teams_webhook_url} variant="secondary" data-testid="teams-nudge-now" className="rounded-sm bg-[#e6e5df] hover:bg-[#cfcfc9] text-[#232826] border border-[#cfcfc9] uppercase text-xs tracking-widest">
            <BellRing size={13} className="mr-2" /> {nudging ? "Sending…" : "Nudge stale items"}
          </Button>
        </div>
      </div>
    </div>
  );
}
